import snakeCase from 'lodash/snakeCase';
import {useCallback, useContext, useState} from 'react';
import {FabricInvitationContext} from '~/common/FabricInvitation/context/FabricInvitationContext';
import {useUyoValidation} from '~/common/FabricInvitation/EventCreateDesign/hooks/useUyoValidation';
import type {FabricInvitationValidationResponse} from '~/common/FabricInvitation/types';
import {ValidationModal} from '~/OneStepCreateInvitation/components/ValidationModal/ValidationModal';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';

const scrollToError = (error: FabricInvitationValidationResponse) => {
  const scrollDiv = document.querySelector('.scrollable');
  if (error.ref) {
    if (error.ref.tagName.toLowerCase() === 'button') {
      error.ref.click();
    } else {
      error.ref.scrollIntoView({block: 'center', behavior: 'smooth'});
    }
  } else {
    const top = error.top || 100;
    scrollDiv?.scroll({top: top - 100, behavior: 'smooth'});
  }
  error.callback?.();
};

const buildMessage = (errors: FabricInvitationValidationResponse[], onClose: () => void) => {
  const errorMessages = errors
    .sort((a, b) => a.order - b.order)
    .map((error, index) => {
      const errorMessage = error.getMessage(index);

      const onClick = () => {
        onClose();
        scrollToError(error);
      };

      const dataQaId = snakeCase(errorMessage);

      return (
        <li key={errorMessage}>
          <Button onClick={onClick} data-qa-id={dataQaId} variant="transparent">
            <Typography variant="list2" color="link" size="large">
              {errorMessage}
            </Typography>
          </Button>
        </li>
      );
    });

  return (
    <div className="onestep-invitation-validation-modal__error">
      <Typography variant="paragraph2">
        To finish your invitation, please complete the following items:
      </Typography>
      <ul>{errorMessages}</ul>
    </div>
  );
};

export const useValidation = () => {
  const [error, setError] = useState<{
    errors: FabricInvitationValidationResponse[];
    error: boolean;
    title: string;
    message: React.ReactNode;
  } | null>(null);
  const {formValidations} = useContext(FabricInvitationContext);
  const {validate: uyoValidation} = useUyoValidation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const close = useCallback(() => setError(null), [setError]);

  const goToFirstError = useCallback(() => {
    setError(null);
    const firstError = error?.errors.sort((a, b) => a.order - b.order).at(0);
    if (!firstError) return;
    scrollToError(firstError);
  }, [error]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const validate = useCallback(() => {
    const errors = Object.values(formValidations.current)
      .map((fn) => fn())
      .filter(Boolean) as FabricInvitationValidationResponse[];

    if (errors.length) {
      setError({
        errors,
        error: true,
        title: 'Missing Details',
        message: buildMessage(errors, close),
      });
    }

    return errors.length;
  }, [uyoValidation, setError]);

  const ErrorModal = (
    <ValidationModal error={error} goToFirstError={goToFirstError} onClose={close} />
  );

  return {validate, error, ErrorModal};
};
